import DeviceSdk from "aws-iot-device-sdk";
import * as log from "loglevel";
import { Hub } from "aws-amplify";

let instance = null;
/**
 * Singleton class to hold mqtt device client instance
 */
export default class IoTClient {
  /**
   * Constructor
   *
   * @params {boolean} createNewClient - Whether or not to use existing client instance
   */
  constructor(createNewClient = false, options = {}) {
    if (createNewClient && instance) {
      instance.disconnect();
      instance = null;
    }

    if (instance) {
      return instance;
    }
    instance = this;
    this.initClient(options);
  }

  // myWebsocketCallback = data => {
  // };

  /**
   * Instantiate AWS IoT device object
   * Note that the credentials must be initialized with empty strings;
   * When we successfully authenticate to the Cognito Identity Pool,
   * the credentials will be dynamically updated.
   *
   * @params {Object} options - Options to pass to DeviceSdk
   */
  initClient(options) {
    // var config = await getHotelConfig();
    this.client = DeviceSdk.device({
      region: options.region,

      // AWS IoT Host endpoint
      host: options.host,
      qos: 1,
      // MQTT Broker assigns random clientId if it is empty
      clientId: options.clientId || "", // options.clientId || clientId, // clientId created earlier

      // Connect via secure WebSocket
      protocol: options.protocol || "wss",

      // set to 'false' to establish a persistent session (default 'true')
      clean: true,

      // Set the maximum reconnect time to 500ms; this is a browser application
      // so we don't want to leave the user waiting too long for reconnection after
      // re-connecting to the network/re-opening their laptop/etc...
      // the base reconnection time in milliseconds (default 1000)
      baseReconnectTimeMs: options.baseReconnectTimeMs || 1000,

      // the maximum reconnection time in milliseconds (default 128000)
      maximumReconnectTimeMs: options.maximumReconnectTimeMs || 5000,

      // Enable console debugging information
      debug: typeof options.debug === "undefined" ? true : options.debug,

      // AWS access key ID, secret key and session token must be
      // initialized with empty strings
      accessKeyId: options.accessKeyId || "",
      secretKey: options.secretKey || "",
      sessionToken: options.sessionToken || "",

      keepAlive: 300,

      // websocketOptions: {
      //   options: { host: options.host, port: 443, clientTracking: true },
      //   callback: this.myWebsocketCallback
      // },
      // Set to 'true' to automatically re-subscribe to topics after reconnection (default 'true')
      autoResubscribe: true,

      // set to 'true' to automatically queue published messages while offline (default 'true')
      offlineQueueing: false

      // set to 'oldest' or 'newest' to define drop behavior on a full queue when offlineQueueMaxSize > 0
      // offlineQueueDropBehavior: "oldest"
    });
  }

  /**
   * Disconnect client
   */
  disconnect() {
    if (this.client) {
      this.client.end();
    }
  }

  /**
   * Update device client with AWS identity credentials after logging in.
   *
   * @param {string} accessKeyId - Access Key Id
   * @param {string} secretAccessKey - Secret Access Key
   * @param {string} sessionToken - Session Token
   */
  updateWebSocketCredentials(accessKeyId, secretAccessKey, sessionToken) {
    this.client.updateWebSocketCredentials(accessKeyId, secretAccessKey, sessionToken);
  }

  /**
   * Attach a message handler
   *
   * @param {IoTClient~onMessageCallback} onNewMessageHandler - Callback that handles a new message
   * @callback IoTClient~onMessageCallback
   *
   * @param {string} topic - Message topic
   * @param {string} jsonPayload - Json encoded message payload
   */
  attachMessageHandler(onNewMessageHandler) {
    this.client.on("message", (topic, jsonPayload) => {
      try {
        const payload = JSON.parse(jsonPayload.toString());
        Hub.dispatch("IOTChannel", {
          event: "messageReceived",
          data: { topic, payload },
          message: `MessageReceived`
        });
      } catch (err) {
        console.error("iot json parse error", err);
      }
    });
  }

  /**
   * Attach a connect handler
   *
   * @param {IoTClient~onConnectHandler} onConnectHandler - Callback that handles a new connection
   *
   * @callback IoTClient~onConnectHandler
   * @param {Object} connack - Connack object
   */
  attachConnectHandler() {
    this.client.on("connect", connack => {
      Hub.dispatch("IOTChannel", {
        event: "connected",
        data: { connack },
        message: `Connected`
      });
    });
  }

  attachReconnectHandler() {
    this.client.on("reconnect", () => {
      Hub.dispatch("IOTChannel", {
        event: "reconnecting",
        data: {},
        message: `Reconnecting`
      });
      // onReconnectHandler();
    });
  }

  attachErrorHandler() {
    this.client.on("error", err => {
      log.error("error", err);
      Hub.dispatch("IOTChannel", {
        event: "error",
        data: {},
        message: `Error occurred${err.message}`
      });
      // onErrorHandler(err);
    });
  }

  attachOfflineHandler() {
    this.client.on("offline", () => {
      Hub.dispatch("IOTChannel", {
        event: "offline",
        data: {},
        message: `Offline`
      });
      // onOfflineHandler();
    });
  }

  /**
   * Attach a close handler
   *
   * @param {IoTClient~onCloseHandler} onCloseHandler - Callback that handles closing connection
   *
   * @callback IoTClient~onCloseHandler
   * @param {Object} err - Connection close error
   */
  attachCloseHandler() {
    this.client.on("close", err => {
      Hub.dispatch("IOTChannel", {
        event: "closed",
        data: {},
        message: `Closed`
      });
      // onCloseHandler(err);
    });
  }

  simulateNetworkFailure() {
    this.client.simulateNetworkFailure();
  }

  /**
   * Publish to an MQTT topic
   *
   * @param {string} topic - Topic to publish to
   * @param {string} message - JSON encoded payload to send
   */
  publish(topic, message) {
    this.client.publish(topic, message);
  }

  subscriptionCallback = info => { };

  /**
   * Subscribe to an MQTT topic
   *
   * @param {string} topic - Topic to subscribe to
   */
  subscribe(topic) {
    if (this.client) this.client.subscribe(topic, { qos: 1 }, this.subscriptionCallback);
  }

  /**
   * Unsubscribe from MQTT topic
   *
   * @param {string} topic - Topic to unsubscribe from
   */
  unsubscribe(topic) {
    if (this.client) {
      this.client.unsubscribe(topic);
    } else {
      log.error("client does not exist. cannot unsubscribe", topic);
    }
  }
}
